import React, {useEffect, useRef, useState} from "react";

import {BaseProps} from "../../../../infrastructure/models/props/base-props";
import {PlayersStatisticsTableColumnViewModel} from "../../../../domain/yard/lineups/players-statistics/tables/columns/players-statistics.table.column.view-model";
import {PlayersStatisticsTableRowSummaryRowViewModel} from "../../../../domain/yard/lineups/players-statistics/tables/rows/summary/players-statistics.table.row.summary-row.view-model";
import {PlayersStatisticsTableRowMemberRowViewModel} from "../../../../domain/yard/lineups/players-statistics/tables/rows/member/players-statistics.table.row.member-row.view-model";
import {BOX_SCORES_WIDGET} from "../../box-scores-widget-consts";
import {BoxScoresWidgetTableStatsBackwardArrowDesktop} from "../table-arrows/box-scores-widget.table-stats.backward-arrow.desktop";
import {BoxScoresWidgetTableStatsColumns} from "./box-scores-widget.table-stats.columns";
import {BoxScoresWidgetTableStatsRows} from "./box-scores-widget.table-stats.rows";
import {BoxScoresWidgetTableStatsSummaries} from "./box-scores-widget.table-stats.summaries";
import {BoxScoresWidgetTableStatsForwardArrowDesktop} from "../table-arrows/box-scores-widget.table-stats.forward-arrow.desktop";
import type {
  PlayersStatisticsTableColumnGroupViewModel
} from "../../../../domain/yard/lineups/players-statistics/tables/columnsGroups/players-statistics.table.column-group.view-model";
import {BoxScoresWidgetTableStatsColumnsGroups} from "./box-scores-widget.table-stats.columns-groups";
import classnames from "classnames";
import styles from "../../styles/box-scores-widget.module.scss";
import {SCROLLABLE_TABLE} from "../../../../core/components/scrollable-table/scrollable-table.consts";
import {ScrollableShadow} from "../../../../core/components/scrollable-table/shadow/scrollable-shadow";

class BoxScoresWidgetTableStatsProps extends BaseProps {
  columns: Array<PlayersStatisticsTableColumnViewModel>;
  rows: Array<PlayersStatisticsTableRowMemberRowViewModel>;
  summary: Array<PlayersStatisticsTableRowSummaryRowViewModel>;
  columnGroups: Array<PlayersStatisticsTableColumnGroupViewModel>;
  isExpandable: Boolean;
  isSmallView: Boolean;
  isRtl: Boolean;
  showAllStats: Number;
}

export const BoxScoresWidgetTableStats = (({controller, table, showAllStats, id}: BoxScoresWidgetTableStatsProps) => {
  const {device, state} = controller;
  const {isRtl, isSmallView} = device;
  const {isExpandable, tableCellWidthDesktop} = state;
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [position, setPosition] = useState(BOX_SCORES_WIDGET.TABLE_POSITION.startPosition);
  const [offset, setOffset] = useState(0);
  const shouldRenderBoxScoresArrows: Boolean = !isExpandable && !isSmallView && !(position <= offset && position >= BOX_SCORES_WIDGET.TABLE_POSITION.startPosition);
  const tableCellWidth = tableCellWidthDesktop;

  useEffect(() => {
    const containerWidth = parseInt(ref?.current?.parentNode.clientWidth);
    const refWidth = parseInt(ref?.current?.clientWidth);

    setOffset(containerWidth - refWidth);
  } , [ref]);

  useEffect(() => {
    setPosition(BOX_SCORES_WIDGET.TABLE_POSITION.startPosition);
    containerRef.current.scrollTo(0, 0);
  }, [id]);

  const relativeContainerProps = {
    className: classnames(styles.table_stats_container, {[styles.arrows_includes]: shouldRenderBoxScoresArrows}),
    ref: containerRef,
  }

  const statsTableProps = {
    className: classnames(styles.expandable_table_stats, {[styles.arrows_includes]: shouldRenderBoxScoresArrows}),
    style: {
      [isRtl ? 'right' : 'left']: position,
    },
    ref
  }

  const forwardArrowProps = {
    className: classnames(styles.table_arrow, {[styles.table_arrow_disable]: position <= offset, [styles.clickable]: position > offset }),
    isRtl,
    onClickNext: () => onClickNext()
  }

  const backwardArrowProps = {
    className: classnames(styles.table_arrow, {[styles.table_arrow_disable]: position >= BOX_SCORES_WIDGET.TABLE_POSITION.startPosition, [styles.clickable]: position < BOX_SCORES_WIDGET.TABLE_POSITION.startPosition}),
    isRtl,
    onClickPrev: () => onClickPrev()
  }

  const onClickPrev = () => {
    if (position < BOX_SCORES_WIDGET.TABLE_POSITION.startPosition) {
      setPosition(position + tableCellWidth)
    }
  }

  const onClickNext = () => {
    if (position > offset) {
      setPosition(position - tableCellWidth)
    }
  }

  const columnProps = {
    columns: table.columns,
    showAllStats: showAllStats,

  }

  const columnGroupProps = {
    columnGroups: table.columnGroups,
    showAllStats: showAllStats,
  }

  const rowProps = {
    rows: table.rows,
    showAllStats: showAllStats,
    isGroupedColumn: table?.columnGroups?.length > 0
  }

  const summaryProps = {
    summary: table.summary,
    showAllStats: showAllStats
  }


  const shadowProps = {
    scrollableElement: containerRef.current,
    showShadow: isSmallView,
    firstShadowMaxWidth: SCROLLABLE_TABLE.SHADOWS.firstMaxWidth,
    lastShadowMaxWidth: SCROLLABLE_TABLE.SHADOWS.lastMaxWidth,
    leftOffset: 142
  }

  return (
    <ScrollableShadow {...shadowProps}>
      {
        shouldRenderBoxScoresArrows && <BoxScoresWidgetTableStatsBackwardArrowDesktop {...backwardArrowProps}/>
      }
      <div {...relativeContainerProps}>
        <div {...statsTableProps}>
          {
              table.columnGroups ? <BoxScoresWidgetTableStatsColumnsGroups {...columnGroupProps}/> : <BoxScoresWidgetTableStatsColumns {...columnProps}/>
          }
          <BoxScoresWidgetTableStatsRows {...rowProps}/>
          <BoxScoresWidgetTableStatsSummaries {...summaryProps}/>
        </div>
      </div>
      {
        shouldRenderBoxScoresArrows && <BoxScoresWidgetTableStatsForwardArrowDesktop {...forwardArrowProps}/>
      }
    </ScrollableShadow>
  )
});
